import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";
import {consume} from "@lit/context";
import {t} from "i18next";
import {LitElement, html, nothing, unsafeCSS, type PropertyValues} from "lit";
import {customElement, state} from "lit/decorators.js";
import {ifDefined} from "lit/directives/if-defined.js";
import {unsafeStatic} from "lit/static-html.js";

import type {BridgeBarConfig} from "../../config";
import {BridgeBarConfigContext} from "../../config";
import type {
  BridgeBarDataContextValue,
  CommonOrgEntities,
  CommonOrgEntity,
  MembershipDataContextValue,
  OrgSearchFn,
  OrgSearchTarget
} from "../../backend";
import {
  BridgeBarDataContext,
  createOrgSearch,
  MAX_SEARCH_QUERY_LENGTH,
  MembershipDataContext,
  MIN_SEARCH_QUERY_LENGTH,
  toPartitionQueryParameter
} from "../../backend";
import {
  COLLAPSE_COMPONENTS_EVENT,
  ORG_SELECTOR_PRESENCE_EVENT,
  ROOT_TAG
} from "../../globals";
import {
  clearSelections,
  dispatchContextChanged,
  readSelection,
  resolveDefault,
  saveSelection
} from "../../lib/contextSelection";
import type {
  ContextChangeSource,
  SelectionScope
} from "../../lib/contextSelection";
import {features, LDClientContext} from "../../launchdarkly";
import type {LDClientContextValue} from "../../launchdarkly";
import type {Environment} from "../../runtime";
import {RuntimeContext} from "../../runtime";
import styles from "./OrgContextSelectorElement.css?inline";

const tagName = `${ROOT_TAG}-org-context-selector`;
export const OrgContextSelector = unsafeStatic(tagName);

/**
 * Shared classes for the active-entity label, used by both the single-entity
 * (static text) and multi-entity (dropdown trigger) renderings.
 *
 * Type is on-surface/default (black) SemiBold 14px per the Command Center mock;
 * `font-bridge` is the project's Roboto stack (the mock specifies Roboto Flex,
 * which this project substitutes with Roboto, as the dealer label this replaces
 * already did).
 *
 * `truncate` needs a bounded width to bite on, so the label is capped rather
 * than allowed to grow with the org name. The caps are derived from the space
 * the bar actually has at each breakpoint:
 *
 *  - In flow (below lg): 180px is the label's preferred width, sized so the row
 *    fits a 375px viewport. It is a ceiling, not a fixed width — the container
 *    designates this selector as the row's one flexible item, so when the row is
 *    tight (a side-nav present, say) the label shrinks below 180px and truncates
 *    harder rather than pushing anything off screen.
 *  - Centered (lg and up): the selector is out of flow, so it can no longer
 *    shrink to fit and this cap is what the container's lg centering threshold
 *    is derived from — 2 * (287px left-hand chrome + 168px half-trigger) = 910px,
 *    which lg (960px) clears. Change one and the other must move: they are
 *    deliberately switched on the same breakpoint so the pair stays visible at
 *    both use sites.
 */
const LABEL_CLASSES =
  "truncate min-w-0 max-w-[180px] lg:max-w-[320px] font-bridge text-[14px] font-semibold leading-tight";

/** Below the ~300ms a pause becomes noticeable, so the search still feels immediate. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * What the search field shows below itself. A union so the states cannot combine
 * — "searching" and "no results" at once is the classic type-ahead flicker.
 * `idle` covers every reason no query is in flight: nothing typed
 * (`reason: "prompt"`), too short to send (`"min-length"`), and too long to send
 * (`"max-length"`). All three are decided where the query is read rather than
 * re-derived from it at render time.
 *
 * None of the three is an error. The bounds are the endpoint's, and a query
 * outside them is a query this component declines to send — not a search that
 * was tried and failed, so none of them offers a retry.
 */
type SearchState =
  | {status: "idle"; reason: "prompt" | "min-length" | "max-length"}
  | {status: "searching"}
  | {status: "results"; results: CommonOrgEntity[]}
  | {status: "error"};

/**
 * OrgContextSelectorElement renders the organization context selector
 * in the Bridge Bar: a trigger showing the active entity and an inline
 * `ids-listbox` overlay of the user's entities (clones
 * ApplicationSwitcherElement's overlay + collapse protocol).
 *
 * It consumes MembershipDataContext (the user's CommonOrgEntities) and
 * BridgeBarDataContext (BridgeUser.id = platformId, for namespacing). On
 * first load with no stored selection it defaults to the first-alphabetical
 * entity and shows an onboarding prompt, emitting the context event with
 * `source: "default"`; on selection it persists per-tab and emits with
 * `source: "user-selection"`. On membership error it renders a non-fatal error
 * state and never throws — an unhandled throw here crashes the whole bar.
 *
 * Which mode it renders is the response's decision, never its own.
 * `showSearch: true` gives an editable type-ahead field. Anything else gives
 * the dropdown over the user's own memberships. The element holds no notion of
 * an internal or external user.
 *
 * How WIDE that field searches is not on the wire and not this element's
 * business. The API scopes each query to the caller: an employee matches the
 * whole hierarchy, a membership too large for the dropdown matches only its own
 * orgs. Both send the identical `{entities: [], showSearch: true}`, so this
 * element cannot tell them apart and must not try.
 * The search behind the field is the Bridge Bar API's
 * `GET /context/orgs/search/mine` — see {@link searchOrgs}.
 *
 * @fires component-expanded - { detail: { component: 'org-context-selector' }, composed, bubbles } when opened.
 * @fires component-collapsed - same shape, when closed.
 * @fires bridge-bar-context-changed - { detail: { commonOrgId, displayName, source }, composed, bubbles }.
 */
@customElement(tagName)
export class OrgContextSelectorElement extends LitElement {
  static styles = unsafeCSS(styles);

  @consume({context: MembershipDataContext, subscribe: true})
  private membership: MembershipDataContextValue | undefined;

  @consume({context: BridgeBarDataContext, subscribe: true})
  private bridgeBarData: BridgeBarDataContextValue | undefined;

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig | undefined;

  @consume({context: LDClientContext, subscribe: true})
  private ldContextValue: LDClientContextValue | undefined;

  /*
   * Consumed for the API base URL and the partition the search must be scoped to — the same two
   * values MembershipDataProviderElement resolves its own fetch from, read the same way so the
   * membership decision authorizing the search and the search itself cannot disagree about
   * which partition they are talking about.
   */
  @consume({context: RuntimeContext, subscribe: true})
  private runtimeEnvironment:
    | RuntimeEnvironment<Environment, unknown>
    | undefined;

  @state()
  private isOpen = false;

  /**
   * The entity currently in context. The whole entity rather than its id because
   * search results are transient — the selection has to outlive the set it came
   * from, and there is no list to look an id up in.
   */
  @state()
  private selectedEntity: CommonOrgEntity | undefined;

  /** True while the active selection is the first-load default. */
  @state()
  private isDefault = false;

  /** The field's value: what the user typed, or the selected entity's name. */
  @state()
  private searchQuery = "";

  /**
   * Whether the text in {@link searchQuery} was typed by the user, rather than put
   * there by a selection.
   *
   * The field has one value but two meanings, and they are indistinguishable by
   * looking at it: "COX AUTOMOTIVE INC." can be the entity currently in context, or
   * it can be something the user typed out by hand. Three behaviours turn on which
   * one it is:
   *
   *  - **Focus** selects the text so the next keystroke replaces it, but only when
   *    the user did not type it. Otherwise tabbing away and back would wipe a
   *    half-finished query.
   *  - **Closing** restores the entity actually in context, but only when the user
   *    typed over it. Otherwise closing an untouched field would rewrite it with
   *    the value it already holds.
   *  - **The popover prompt** says "search to get started" for an untouched field.
   *    Saying that under a name the user just typed would be a non-sequitur.
   *
   * Set true on input, false whenever a selection supplies the text.
   */
  @state()
  private queryWasTyped = false;

  @state()
  private searchState: SearchState = {status: "idle", reason: "prompt"};

  /** Keyboard position in the current results, or -1. Reset when they change. */
  @state()
  private activeResultIndex = -1;

  /**
   * How the selector searches for entities. Every search-mode state runs through
   * it, so this element knows about a promise of entities and not about HTTP.
   * Public so a test can substitute one that resolves, rejects, or never
   * settles, rather than intercepting the network.
   */
  searchOrgs: OrgSearchFn = createOrgSearch(() => this.orgSearchTarget);

  /** Settable so a test can run the search on the keystroke. */
  searchDebounceMs = SEARCH_DEBOUNCE_MS;

  private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  private searchAbort: AbortController | undefined;

  /**
   * Which search is current; a resolution carrying an older number is dropped.
   * Aborting is not enough, because honouring the signal is optional — a search
   * that ignores it could let a slow early query overwrite a fast later one.
   */
  private searchRequestId = 0;

  /** Guards the once-only initial selection resolution + event emission. */
  private initialized = false;

  /** The membership data last checked by {@link revalidateSelection}. */
  private lastRevalidatedMembership: CommonOrgEntities | undefined;

  /** Last presence value announced to the container; undefined = never sent. */
  private announcedPresence: boolean | undefined;

  private handleDocumentClick: (e: MouseEvent) => void;
  private handleDocumentKeyDown: (e: KeyboardEvent) => void;
  private handleCollapseRequest: () => void;

  constructor() {
    super();
    this.handleDocumentClick = (e: MouseEvent) => {
      if (!e.composedPath().includes(this)) {
        this.closeMenu();
      }
    };
    this.handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.isOpen) {
        this.closeMenu();
      }
    };
    this.handleCollapseRequest = () => {
      this.closeMenu();
    };
  }

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
    document.addEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.removeEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
    // A pending search would set state on a detached element.
    this.cancelPendingSearch();
  }

  private get platformId(): string | undefined {
    return this.bridgeBarData?.status === "complete"
      ? this.bridgeBarData.data.id
      : undefined;
  }

  /**
   * Scope the persisted selection is stored under, or undefined while either
   * input is still missing (user details not yet fetched, config not yet
   * provided). See {@link SelectionScope} for why the login is part of it.
   */
  private get selectionScope(): SelectionScope | undefined {
    const platformId = this.platformId;
    const sessionId = this.config?.sessionId;
    if (!platformId || !sessionId) return undefined;
    return {platformId, sessionId};
  }

  /**
   * Where an entity search should be sent, or undefined while the runtime is still resolving.
   *
   * Read per search rather than captured once: the runtime arrives asynchronously, and an
   * adopting page may change partition without remounting the bar. A search issued before it
   * resolves fails, which is honest — there is nowhere to ask yet — but it is also not reachable
   * in practice, since the search field only renders once the membership response has arrived,
   * and that fetch needs the same runtime.
   */
  private get orgSearchTarget(): OrgSearchTarget | undefined {
    const runtimeEnvironment = this.runtimeEnvironment;
    const baseUrl = runtimeEnvironment?.environment?.bridgeBarApiUrl;
    if (!baseUrl) return undefined;
    return {
      baseUrl,
      partitionQueryParameter: toPartitionQueryParameter(
        runtimeEnvironment.runtime.partition
      )
    };
  }

  /**
   * Whether the chosen entity is persisted across page loads, or kept in
   * memory for the life of the page only.
   *
   * Defaults to **false**: persistence is opt-in, so unless the flag is
   * explicitly on the selection is held in memory only and every page load
   * resolves the first-alphabetical default.
   *
   * The value is read at initialisation rather than cached because it is only
   * consulted once the membership context is complete — and memberships cannot
   * resolve until the selector's own LD gate has passed, so the client is
   * guaranteed ready and this never evaluates against pre-identify flags.
   */
  private get persistenceEnabled(): boolean {
    return (
      this.ldContextValue?.ldClient?.variation(
        features.dev.orgContextPersistence,
        false
      ) === true
    );
  }

  /**
   * The org the response says to select initially, when it says so.
   *
   * Only meaningful in search mode. Not conditional on `entities` being empty: in search
   * mode `entities` is a partial list at best — the searchable set is not enumerated — so a
   * non-empty list does not mean the default is redundant, and the default need not appear in
   * it.
   */
  private get defaultEntity(): CommonOrgEntity | undefined {
    if (this.membership?.status !== "complete") {
      return undefined;
    }
    const {showSearch, defaultEntity} = this.membership.data;
    return showSearch === true ? defaultEntity : undefined;
  }

  /** Whether the response asks for type-ahead search instead of a dropdown. */
  private get isSearchMode(): boolean {
    return (
      this.membership?.status === "complete" &&
      this.membership.data.showSearch === true
    );
  }

  /**
   * The orgs the dropdown offers. Empty in search mode whatever `entities` holds:
   * there it is a partial list at best, so rendering it would offer a list that
   * silently omits most of what the user can reach.
   */
  private get entities(): CommonOrgEntity[] {
    if (this.membership?.status !== "complete" || this.isSearchMode) {
      return [];
    }
    return this.membership.data.entities;
  }

  /** Every org the response named, in either field. Turns a stored id back into an entity. */
  private get knownEntities(): CommonOrgEntity[] {
    if (this.membership?.status !== "complete") {
      return [];
    }
    const {entities} = this.membership.data;
    const def = this.defaultEntity;
    if (!def || entities.some(e => e.commonOrgId === def.commonOrgId)) {
      return entities;
    }
    return [...entities, def];
  }

  override willUpdate(_changed: PropertyValues): void {
    // Reflect theme onto the host before paint (matches sibling components;
    // :host-context() is unsupported in Firefox).
    const mode = this.config?.theme ?? "light";
    if (this.getAttribute("data-ids-theme-mode") !== mode) {
      this.setAttribute("data-ids-theme-mode", mode);
    }
  }

  override updated(changed: PropertyValues): void {
    // Resolve the initial selection once both contexts are ready. Guarded
    // side-effect in updated() mirrors the container's initBbShown pattern.
    this.maybeInitializeSelection();
    this.revalidateSelection();
    this.announcePresence();
    if (changed.has("activeResultIndex")) {
      this.scrollActiveResultIntoView();
    }
  }

  /**
   * Keep the arrow-key highlight on screen.
   *
   * One query returns up to {@link SEARCH_RESULT_LIMIT} rows, and the list is
   * capped at `max-h-80` — around eight of them. Without this the highlight walks
   * out of the scroll box and the sighted keyboard user loses track of it, while
   * `aria-activedescendant` keeps naming rows a screen-reader user hears
   * perfectly well. The two have to agree.
   *
   * `block: "nearest"` scrolls the list only as far as it must, so a row already
   * in view does not jerk to the middle, and the host page never scrolls.
   */
  private scrollActiveResultIntoView(): void {
    // Not load-bearing — `#org-selector-result--1` matches nothing, so the query
    // below already fails safe. It states the intent, and skips a shadow-root
    // query on every keystroke, since each one clears the highlight to -1.
    if (this.activeResultIndex < 0) return;
    this.shadowRoot
      ?.querySelector(`#org-selector-result-${this.activeResultIndex}`)
      ?.scrollIntoView({block: "nearest"});
  }

  /**
   * Drops the active selection if a membership refresh removed it from the
   * dropdown's current orgs, rather than keep showing an entity the user can
   * no longer reach. Dropdown mode only: in search mode `entities` is a
   * partial list, so a searched-in org missing from it is expected, not
   * stale (see {@link knownEntities}).
   */
  private revalidateSelection(): void {
    if (this.membership?.status !== "complete") return;
    if (this.isSearchMode) return;
    if (this.membership.data === this.lastRevalidatedMembership) return;
    this.lastRevalidatedMembership = this.membership.data;

    if (
      this.selectedEntity &&
      !this.entities.some(
        e => e.commonOrgId === this.selectedEntity?.commonOrgId
      )
    ) {
      this.selectedEntity = undefined;
    }
  }

  /**
   * Tells the container whether this selector has anything to show, so it can
   * decide whether the logo needs to yield space for it.
   *
   * "Present" means this component puts something in the slot — which includes a
   * resolved-but-empty membership, since that renders the no-entities message.
   * Those users therefore get the same treatment as everyone else: below 520px
   * the message owns the row and the logo yields. Reserving the logo's space for
   * them instead would leave them the one group who never sees why their bar has
   * no entity on a narrow viewport, which is the whole point of the message.
   *
   * The definitive "no" is a missing user identity: without `platformId` render()
   * returns nothing, so there is genuinely nothing for the logo to yield to.
   * Everything still in flight (fetching, rollout flag off) reports nothing at
   * all — the container assumes a selector is coming, which keeps the logo from
   * appearing and then disappearing on the common path. Emitted from updated(),
   * never render().
   */
  private announcePresence(): void {
    if (this.membership?.status !== "complete") {
      return;
    }
    const present = !!this.platformId;
    if (this.announcedPresence === present) {
      return;
    }
    this.announcedPresence = present;
    this.dispatchEvent(
      new CustomEvent(ORG_SELECTOR_PRESENCE_EVENT, {
        detail: {present},
        bubbles: true,
        composed: true
      })
    );
  }

  /**
   * On first readiness: restore the stored per-tab selection if it is still a
   * valid membership, otherwise default to the first-alphabetical entity
   * Either way emit the context event so adopters scope correctly on
   * load — `source: "default"` for the fallback, `"user-selection"` for a
   * restored prior choice. Runs once (guarded by `initialized`).
   */
  private maybeInitializeSelection(): void {
    if (this.initialized) return;
    if (this.membership?.status !== "complete") return;
    const scope = this.selectionScope;
    if (!scope) return; // wait for user details (BridgeUser.id) + config

    // Nothing named in either field: no selection to resolve. In search mode this
    // is the no-default case, where the user's first search supplies it.
    const knownEntities = this.knownEntities;
    if (knownEntities.length === 0) {
      this.initialized = true;
      return;
    }

    // With persistence off, ignore anything stored and drop it, so the demo
    // starts from a genuinely clean slate every load.
    if (!this.persistenceEnabled) {
      clearSelections(scope.platformId);
    }

    const stored = this.persistenceEnabled ? readSelection(scope) : null;
    const storedEntity = stored
      ? knownEntities.find(e => e.commonOrgId === stored)
      : undefined;

    this.initialized = true;

    if (storedEntity) {
      this.applySelection(storedEntity, "user-selection");
      return;
    }

    // The response's own default beats first-alphabetical; a stored choice beats
    // both, handled above. `entities` is empty in search mode, so there the
    // response's default is the only one there can be.
    const def = this.defaultEntity ?? resolveDefault(this.entities);
    if (def) {
      this.applySelection(def, "default");
      this.isDefault = true;
    }
  }

  /**
   * Make an entity the active context: show it, and tell adopters. Seeds the
   * search field so a search-mode user sees what they are scoped to rather than
   * an empty box. Sets `isDefault` false; the one caller meaning otherwise sets
   * it back.
   */
  private applySelection(
    entity: CommonOrgEntity,
    source: ContextChangeSource
  ): void {
    this.selectedEntity = entity;
    this.isDefault = false;
    this.searchQuery = entity.displayName;
    this.queryWasTyped = false;
    dispatchContextChanged(this, {
      commonOrgId: entity.commonOrgId,
      displayName: entity.displayName,
      source
    });
  }

  private handleTriggerClick(): void {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  private openMenu(): void {
    // Guarded like closeMenu(): this runs on every keystroke, and an unguarded
    // dispatch would ask every sibling to collapse once per character.
    if (this.isOpen) return;
    this.isOpen = true;
    this.dispatchEvent(
      new CustomEvent("component-expanded", {
        detail: {component: "org-context-selector"},
        composed: true,
        bubbles: true
      })
    );
  }

  private closeMenu(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.abandonSearch();
    this.dispatchEvent(
      new CustomEvent("component-collapsed", {
        detail: {component: "org-context-selector"},
        composed: true,
        bubbles: true
      })
    );
  }

  /**
   * Put the field back to the entity actually in context, discarding a half-typed
   * query. Closing without picking is a cancel, and the field is the only place
   * the active entity is named — leaving "acm" in it misstates the context.
   *
   * Hung off closing rather than blur: blur fires on mousedown over a result row,
   * before its click, so clearing there would make results unselectable by mouse.
   */
  private abandonSearch(): void {
    if (!this.queryWasTyped) return;
    this.cancelPendingSearch();
    this.searchQuery = this.selectedEntity?.displayName ?? "";
    this.queryWasTyped = false;
    this.searchState = {status: "idle", reason: "prompt"};
    this.activeResultIndex = -1;
  }

  private handleSelect(entity: CommonOrgEntity): void {
    const scope = this.selectionScope;
    if (scope && this.persistenceEnabled) {
      saveSelection(scope, entity.commonOrgId);
    }
    this.applySelection(entity, "user-selection");
    // The field now shows the chosen entity, so stale results would not match it.
    this.cancelPendingSearch();
    this.searchState = {status: "idle", reason: "prompt"};
    this.activeResultIndex = -1;
    this.closeMenu();
  }

  /**
   * Drop any search in flight or waiting to start. Bumps the request id so a
   * resolution already queued as a microtask is discarded when it arrives.
   */
  private cancelPendingSearch(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = undefined;
    this.searchAbort?.abort();
    this.searchAbort = undefined;
    this.searchRequestId++;
  }

  private handleSearchInput(e: Event): void {
    this.searchQuery = (e.target as HTMLInputElement).value;
    this.queryWasTyped = true;
    this.openMenu();
    this.cancelPendingSearch();
    this.activeResultIndex = -1;

    // Outside the length the endpoint accepts, so not sent. Idle, not an error,
    // at both ends: nothing was tried, so there is nothing to retry. Too short is
    // the user mid-word, and a message there would be noise on every search.
    const trimmed = this.searchQuery.trim();
    if (trimmed.length < MIN_SEARCH_QUERY_LENGTH) {
      this.searchState = {
        status: "idle",
        reason: trimmed.length === 0 ? "prompt" : "min-length"
      };
      return;
    }
    // Sending it anyway earns a 400, which renders as a failed search offering a
    // retry that is guaranteed to fail again.
    if (trimmed.length > MAX_SEARCH_QUERY_LENGTH) {
      this.searchState = {status: "idle", reason: "max-length"};
      return;
    }

    const query = trimmed;
    this.searchDebounceTimer = setTimeout(() => {
      void this.runSearch(query);
    }, this.searchDebounceMs);
  }

  /**
   * Run one search and render its outcome, unless a newer one has started. Never
   * rethrows: a failure renders the retry state, where an unhandled rejection
   * could take the bar with it.
   */
  private async runSearch(query: string): Promise<void> {
    const abort = new AbortController();
    this.searchAbort = abort;
    const requestId = ++this.searchRequestId;
    this.searchState = {status: "searching"};

    try {
      const results = await this.searchOrgs(query, abort.signal);
      if (requestId !== this.searchRequestId) return;
      this.searchState = {status: "results", results};
    } catch (error) {
      if (requestId !== this.searchRequestId) return;
      console.error("[OrgContextSelectorElement] Org search failed:", error);
      this.searchState = {status: "error"};
    }
    this.activeResultIndex = -1;
  }

  /** Re-run the current query after a failure, from the retry button. */
  private handleSearchRetry(): void {
    this.cancelPendingSearch();
    // Trimmed, like the query the failed search sent: a retry has to re-send the
    // same thing, or the button quietly searches for something else.
    void this.runSearch(this.searchQuery.trim());
  }

  /** Opens on focus so the prompt is reachable without typing first. */
  private handleSearchFocus(e: FocusEvent): void {
    this.openMenu();
    // Select the entity name so the next keystroke replaces it, rather than
    // typing into the middle of a name the user did not write. Skipped while
    // a query is mid-typed, so tabbing away and back does not
    // wipe unsubmitted input.
    if (!this.queryWasTyped) {
      (e.target as HTMLInputElement).select();
    }
  }

  /**
   * Closes when focus leaves the field for something outside this component.
   *
   * The counterpart to opening on focus. Without it a keyboard user who tabs in
   * and then tabs onward leaves the popover open behind them — and open means the
   * container is still rendering its page-wide overlay, which swallows the next
   * click anywhere in the host app.
   *
   * Hung off `focusout` on the wrapper rather than `blur` on the field, so a
   * focus move to a result row reads as staying inside. A mouse click on a row
   * focuses that button before its click fires, so closing on the field's own
   * blur would make results unselectable by mouse.
   *
   * A null `relatedTarget` — focus leaving the document, or landing on something
   * unfocusable — deliberately keeps it open: the document click handler already
   * covers a click away, and closing here would fight the browser's own
   * window-blur behaviour.
   */
  private handleFocusOut(e: FocusEvent): void {
    const next = e.relatedTarget as Node | null;
    if (next && !this.shadowRoot?.contains(next)) {
      this.closeMenu();
    }
  }

  private get searchResults(): CommonOrgEntity[] {
    return this.searchState.status === "results"
      ? this.searchState.results
      : [];
  }

  /**
   * Keyboard navigation of the results; Escape is handled document-wide. Arrows
   * move a highlight, not the selection — moving the selection would emit a
   * context change per keypress and re-scope every adopting surface on the way.
   */
  private handleSearchKeyDown(e: KeyboardEvent): void {
    const results = this.searchResults;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (results.length === 0) return;
      e.preventDefault();
      this.openMenu();
      const step = e.key === "ArrowDown" ? 1 : -1;
      // Wraps, treating -1 as before the first row, so ArrowUp lands on the last.
      const next =
        (this.activeResultIndex + step + results.length + 1) %
        (results.length + 1);
      this.activeResultIndex = next === results.length ? -1 : next;
      return;
    }
    if (e.key === "Enter") {
      const active = results[this.activeResultIndex];
      if (active) {
        e.preventDefault();
        this.handleSelect(active);
      }
    }
  }

  /**
   * One selectable org row, shared by the dropdown and the search results.
   * `index` is the position among search results, undefined in the dropdown; it
   * drives the id `aria-activedescendant` points at, so passing it for dropdown
   * rows would make that reference ambiguous.
   */
  private renderOption(entity: CommonOrgEntity, index?: number) {
    const isActive = entity.commonOrgId === this.selectedEntity?.commonOrgId;
    const selectedClasses = isActive
      ? "bg-primary-50 border-0 border-l-4 border-solid border-primary-700 -ml-px dark:bg-primary-600 dark:border-transparent dark:text-white"
      : "";
    // Distinct from the selected row: during a search both can be on screen at
    // once, and the selected entity is the one being replaced.
    const highlightClasses =
      index !== undefined && index === this.activeResultIndex
        ? "!bg-neutral-100 dark:!bg-primary-700"
        : "";
    return html`
      <button
        data-testid="org-selector-option"
        data-common-org-id=${entity.commonOrgId}
        id=${ifDefined(
          index === undefined ? undefined : `org-selector-result-${index}`
        )}
        role="option"
        aria-selected=${isActive ? "true" : "false"}
        class="ids-listbox-item items-center text-left text-primary-700 hover:text-primary-900 dark:text-white dark:hover:text-white dark:hover:!bg-primary-600 bg-transparent cursor-pointer min-w-0 max-w-full pl-6 ${selectedClasses} ${highlightClasses}"
        @click=${() => this.handleSelect(entity)}>
        <!-- A button's auto width is fit-content even under display:flex
             (.ids-listbox-item), so a long name grew the row past the popover
             and was clipped mid-glyph instead of ellipsised. max-w-full caps
             the row at the popover's content width, which gives the span's
             truncate a bound to work against; short rows stay fit-content. -->
        <span class="truncate min-w-0" title=${entity.displayName}
          >${entity.displayName}</span
        >
      </button>
    `;
  }

  /**
   * The chevron shape shared by the dropdown trigger and the search field —
   * both open a list of entities, so both point at it the same way.
   */
  private renderChevronGlyph() {
    return html`
      <span
        aria-hidden="true"
        class="flex-shrink-0 w-2 h-2 border-b-2 border-r-2 border-current rotate-45 -translate-y-1/4 text-primary-700 dark:text-white"></span>
    `;
  }

  /**
   * The popover shell shared by the dropdown and the search results — same
   * positioning and sizing either way; only the content inside differs.
   *
   * Carries no role of its own. `role="listbox"` sits on the row list inside it
   * ({@link renderOptionList}) instead, because a listbox may only own options
   * and groups. The onboarding note and the four search messages are neither,
   * and a live region nested inside a listbox is not reliably announced — which
   * would quietly cost the search states the very announcement `role="status"`
   * is there to get.
   */
  private renderPopoverShell(content: unknown) {
    return html`
      <div
        data-testid="org-selector-popover"
        class="ids-popover ids-listbox min-w-[16rem] max-w-[320px] dark:bg-primary-900 dark:border-primary-700"
        data-open="true"
        data-ids-variant="compact"
        open
        data-ids-pos="bottom start">
        ${content}
      </div>
    `;
  }

  /**
   * The selectable rows, and the only element carrying `role="listbox"`.
   *
   * Rendered even with no rows, so the combobox's `aria-controls` always
   * resolves to a real listbox and the messages beside it never have to live
   * inside one. An empty listbox is valid, and `.ids-listbox-list` is
   * `margin: 0; padding: 0`, so an empty one occupies no space either.
   */
  private renderOptionList(id: string | undefined, rows: unknown) {
    return html`
      <div
        id=${ifDefined(id)}
        role="listbox"
        class="ids-listbox-list max-h-80 overflow-y-auto">
        ${rows}
      </div>
    `;
  }

  private renderPopover() {
    if (!this.isOpen) return nothing;
    return this.renderPopoverShell(html`
      ${this.isDefault
        ? html`
            <div
              data-testid="org-selector-onboarding"
              role="note"
              class="font-sans text-center text-sm text-neutral-600 dark:text-white py-2 px-4">
              ${t("orgContextSelector.onboardingPrompt")}
            </div>
            <div class="ids-listbox-divider"></div>
          `
        : nothing}
      ${this.renderOptionList(
        undefined,
        this.entities.map(e => this.renderOption(e))
      )}
    `);
  }

  /**
   * A non-selectable row in the search popover: prompt, progress, no-results, or
   * failure. `role="status"` for what a search produces, so the outcome is spoken
   * without moving focus off the field; `role="note"` for the prompts, which
   * predate any search and would otherwise be announced as news.
   */
  private renderSearchMessage(
    testId: string,
    message: string,
    role: "note" | "status",
    trailing: unknown = nothing
  ) {
    return html`
      <div
        data-testid=${testId}
        role=${role}
        class="font-sans text-center text-sm text-neutral-600 dark:text-white py-2 px-4">
        ${message}${trailing}
      </div>
    `;
  }

  /**
   * The message under the search field; one state at a time, and `nothing` when
   * there are rows to show instead.
   *
   * Returns the message alone, not the rows: {@link renderSearchPopover} renders
   * the listbox separately, so a message is never a child of it.
   */
  private renderSearchMessageForState() {
    switch (this.searchState.status) {
      case "searching":
        return this.renderSearchMessage(
          "org-selector-search-loading",
          t("orgContextSelector.searchLoading"),
          "status"
        );
      case "error":
        // Recoverable and local: the field keeps the query, the bar is untouched.
        return this.renderSearchMessage(
          "org-selector-search-error",
          t("orgContextSelector.searchError"),
          "status",
          html`
            <button
              data-testid="org-selector-search-retry"
              class="ids-btn ml-2 cursor-pointer"
              data-ids-variant="secondary"
              data-ids-size="sm"
              @click=${() => this.handleSearchRetry()}>
              ${t("orgContextSelector.searchRetry")}
            </button>
          `
        );
      case "results":
        if (this.searchState.results.length === 0) {
          // An answer, not a failure: no error, and the query stays to be narrowed.
          return this.renderSearchMessage(
            "org-selector-search-empty",
            t("orgContextSelector.searchNoResults"),
            "status"
          );
        }
        // The rows themselves carry the state, so there is nothing to say.
        return nothing;
      case "idle":
        // An untouched field is an invitation; a field the component
        // declines to search on has to say why nothing is happening. A field
        // holding a selected entity's name counts as untouched.
        switch (this.searchState.reason) {
          case "prompt":
            return this.renderSearchMessage(
              "org-selector-search-prompt",
              t("orgContextSelector.searchPrompt"),
              "note"
            );
          case "min-length":
            return this.renderSearchMessage(
              "org-selector-search-min-length",
              t("orgContextSelector.searchMinLength"),
              "note"
            );
          case "max-length":
            return this.renderSearchMessage(
              "org-selector-search-max-length",
              t("orgContextSelector.searchMaxLength"),
              "note"
            );
        }
    }
  }

  /**
   * The popover under the search field: the listbox of rows, and beside it the
   * one message that describes the current state.
   *
   * The listbox comes first so the rows keep their position under the field, and
   * it is always present so `aria-controls` always resolves. Exactly one of the
   * two is ever non-empty.
   */
  private renderSearchPopover() {
    if (!this.isOpen) {
      // Closed, but the listbox `aria-controls` names still has to exist. ARIA 1.2
      // makes `aria-controls` a required property of `role="combobox"`, and an IDREF
      // resolving to nothing is its own violation — so dropping either the attribute
      // or its target breaks the contract. An empty `hidden` listbox satisfies both:
      // `aria-expanded="false"` is what says it is collapsed, and `hidden` keeps it
      // out of the accessibility tree and out of the layout.
      return html`<div id="org-selector-results" role="listbox" hidden></div>`;
    }
    return this.renderPopoverShell(html`
      ${this.renderOptionList(
        "org-selector-results",
        this.searchResults.map((e, i) => this.renderOption(e, i))
      )}
      ${this.renderSearchMessageForState()}
    `);
  }

  /**
   * Search mode: an editable field the user types into, with results in a popover
   * below it. The field is the trigger, deliberately not a label with a
   * search box inside the popover — the pattern the mocks reject.
   */
  private renderSearchMode() {
    // Omitted rather than empty: an empty aria-activedescendant is a reference to
    // nothing, which assistive tech has to interpret.
    const activeId =
      this.activeResultIndex >= 0
        ? `org-selector-result-${this.activeResultIndex}`
        : undefined;
    // The placeholder override is not decoration: ids-form-control's one colour
    // for both themes (#6d6d6d) measures 2.4:1 on the dark field and fails WCAG
    // AA; neutral-300 measures 5.9:1. With no Default Entity it is the only label.
    //
    // The wrapping span positions the shared chevron glyph over the field's
    // right edge, and ignores pointer events so a click there still focuses
    // the field.
    //
    // Its vertical alignment was measured and settled 2026-08-20 — do not
    // re-derive it. The line box is centred. All-caps text rides high in it,
    // since it has no descenders. A name with a descender rides equally low.
    // Ink offsets from centre, -0.75 against +0.75, are exact opposites. So
    // the ~1.25px by which the all-caps default reads high is glyph metrics,
    // not a layout bug. Every nudge fixes one kind of entity name by breaking
    // the other. Firefox shows it worse than Chromium: a 15px selection band
    // against 17px, taken off the top. None of that is stylable.
    return html`
      <div
        class="relative min-w-0"
        data-testid="org-context-selector"
        @focusout=${(e: FocusEvent) => this.handleFocusOut(e)}>
        <input
          data-testid="org-selector-search-input"
          type="text"
          role="combobox"
          autocomplete="off"
          spellcheck="false"
          maxlength=${MAX_SEARCH_QUERY_LENGTH}
          aria-autocomplete="list"
          aria-controls="org-selector-results"
          aria-expanded=${this.isOpen ? "true" : "false"}
          aria-activedescendant=${ifDefined(activeId)}
          aria-label=${t("orgContextSelector.searchPlaceholder")}
          placeholder=${t("orgContextSelector.searchPlaceholder")}
          data-ids-size="sm"
          class="ids-form-control ${LABEL_CLASSES} w-full pr-6 dark:bg-primary-900 dark:text-white dark:border-primary-700 dark:placeholder:text-neutral-300"
          .value=${this.searchQuery}
          @click=${() => this.openMenu()}
          @input=${(e: Event) => this.handleSearchInput(e)}
          @focus=${(e: FocusEvent) => this.handleSearchFocus(e)}
          @keydown=${(e: KeyboardEvent) => this.handleSearchKeyDown(e)} />
        <span
          data-testid="org-selector-search-chevron"
          aria-hidden="true"
          class="pointer-events-none absolute inset-y-0 right-2 flex items-center">
          ${this.renderChevronGlyph()}
        </span>
        ${this.renderSearchPopover()}
      </div>
    `;
  }

  override render() {
    // Render a non-fatal error state on membership failure, never throw.
    if (this.membership?.status === "error") {
      return html`
        <div data-testid="org-context-selector" style="display: contents">
          <div
            data-testid="org-selector-error"
            class="ids-type text-xs px-2 text-neutral-600 dark:text-white"
            data-ids-size="sm">
            ${t("orgContextSelector.loadError")}
          </div>
        </div>
      `;
    }

    // Still loading or no user identity yet → render nothing.
    if (this.membership?.status !== "complete" || !this.platformId) {
      return nothing;
    }

    // Before the branches below, which read `entities` — a field search mode does
    // not use, and whose emptiness says nothing about what the user can reach.
    if (this.isSearchMode) {
      return this.renderSearchMode();
    }

    // Memberships resolved but the user belongs to no org. Rendering nothing
    // here is indistinguishable from "still loading" to the user, so state it
    // explicitly instead of leaving the bar looking permanently stuck.
    //
    // Shown at every width. Below 520px it owns the in-flow row and the logo
    // yields, exactly as it does for a user who has entities — see
    // announcePresence(). `truncate` keeps a long translation from widening the
    // bar rather than ellipsising.
    if (this.entities.length === 0) {
      return html`
        <div data-testid="org-context-selector" style="display: contents">
          <div
            data-testid="org-selector-empty"
            role="status"
            class="ids-type text-xs px-2 text-neutral-600 dark:text-white truncate min-w-0"
            data-ids-size="sm">
            ${t("orgContextSelector.noMemberships")}
          </div>
        </div>
      `;
    }

    const active = this.selectedEntity;
    // Single entity: static text, no dropdown — there is nothing to switch to, so a control
    // would be a no-op affordance. Note this also makes the onboarding prompt
    // unreachable for these users, which is intended: it only renders inside the popover, and
    // "select your dealership to get started" has no meaning when the choice is already made
    // for them.
    if (this.entities.length === 1) {
      return html`
        <div class="flex min-w-0" data-testid="org-context-selector">
          <span
            class="${LABEL_CLASSES} text-black dark:text-white"
            title=${active?.displayName ?? ""}
            >${active?.displayName ?? ""}</span
          >
        </div>
      `;
    }
    // Multiple entities: render as dropdown.
    return html`
      <div class="relative min-w-0" data-testid="org-context-selector">
        <button
          data-testid="org-selector-trigger"
          class="flex items-center gap-2 max-w-full bg-transparent border-none cursor-pointer p-0"
          aria-haspopup="listbox"
          aria-expanded=${this.isOpen ? "true" : "false"}
          @click=${() => this.handleTriggerClick()}>
          <!-- Truncated at the cap in LABEL_CLASSES. The title attribute is set
               for every name, not only truncated ones: detecting truncation needs
               a post-render scrollWidth/clientWidth comparison, and a native
               tooltip repeating a fully visible name is harmless enough that the
               measurement is not worth it. -->
          <span
            class="${LABEL_CLASSES} text-black hover:text-primary-700 dark:text-white dark:hover:text-primary-100"
            title=${active?.displayName ?? ""}
            >${active?.displayName ?? ""}</span
          >
          <!-- Chevron carries its own color (interactive blue in light, white in
               dark) so border-current renders it independently of the black
               label. -->
          ${this.renderChevronGlyph()}
        </button>
        ${this.renderPopover()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: OrgContextSelectorElement;
  }
}
